<template>
  <section ref="screenRoot" class="page kanban-screen" data-test="kanban-screen">
    <!-- Hero -->
    <div class="kanban-hero">
      <div class="anim" data-delay="80">
        <Eyebrow pulse>
          Project · {{ projectKey }} · Kanban
        </Eyebrow>
        <DisplayHeading :lead="projectKey" accent="kanban" />
        <p class="display-sub" data-test="kanban-window-line">
          {{ windowLine }}
          <BoardTypeBadge board-type="kanban" />
        </p>
        <RouterLink
          v-if="hasScrum"
          :to="{ name: 'project', params: { key: projectKey } }"
          class="board-switch"
          data-test="switch-to-sprint"
        >← Sprint board</RouterLink>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorObj" class="error-state anim" data-delay="180">
      <p class="error-title">Couldn't load this board</p>
      <p class="error-msg">{{ errorObj.message }}</p>
      <button class="retry" type="button" @click="store.load(projectKey, true)">Retry</button>
    </div>

    <!-- Loading -->
    <div v-else-if="isInitialLoading" class="skeletons anim" data-delay="200" data-test="loading">
      <div class="skel-row big" />
      <div class="skel-row" />
    </div>

    <!-- Empty -->
    <div v-else-if="!windowRow" class="empty anim" data-delay="200" data-test="kanban-empty">
      No Kanban window recorded for {{ projectKey }} yet.
    </div>

    <!-- Body -->
    <div v-else class="kanban-grid anim" data-delay="240" data-test="kanban-grid">
      <article class="now-card" data-test="kanban-now-card">
        <header class="now-head">
          <div>
            <span class="lbl-mono">Now · Rolling window</span>
            <h2 class="now-title">{{ windowRow.sprint_name }}</h2>
            <p class="window-range">{{ rangeLine }}</p>
          </div>
        </header>
      </article>

      <ThroughputTile :throughput="throughput" :wip="wip" />

      <HygieneWatch
        v-if="windowDetail"
        :project-key="projectKey"
        :sprint="windowDetail"
        scope-label="rolling window"
      />

      <JqlFilters scope="project" :project-key="projectKey" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink, useRoute } from "vue-router";
import BoardTypeBadge from "@/components/BoardTypeBadge.vue";
import HygieneWatch from "@/components/HygieneWatch.vue";
import JqlFilters from "@/components/JqlFilters.vue";
import ThroughputTile from "@/features/kanban/components/ThroughputTile.vue";
import DisplayHeading from "@/components/scorecard/DisplayHeading.vue";
import Eyebrow from "@/components/scorecard/Eyebrow.vue";
import { useReveal } from "@/composables/useReveal";
import { fmtDate } from "@/lib/format";
import { useSprintDetailStore } from "@/stores/sprintDetail";
import { useSprintsStore } from "@/stores/sprints";

const route = useRoute();
const store = useSprintsStore();
const detailStore = useSprintDetailStore();
const screenRoot = ref<HTMLElement | null>(null);
useReveal(screenRoot);

const projectKey = computed(() => String(route.params.key ?? ""));
// The single Kanban rolling-window row carries throughput/wip/capitalization
// signals directly on the list item (DashboardSignals).
const windowRow = computed(() => store.latest(projectKey.value, "kanban"));
const hasScrum = computed(() => store.items(projectKey.value, "scrum").length > 0);
const errorObj = computed(() => store.error(projectKey.value));
const isLoading = computed(() => store.loading(projectKey.value));
const isInitialLoading = computed(() => isLoading.value && !windowRow.value);

// Detail (capex + compliance) for the window — fetched on the synthetic id.
const windowDetail = computed(() =>
  windowRow.value ? detailStore.detail(projectKey.value, windowRow.value.sprint_id) : null,
);
// throughput/wip live on metrics in the detail payload; the list row doesn't
// carry them, so prefer the detail and fall back to the list row if present.
const throughput = computed(
  () => windowDetail.value?.metrics?.throughput ?? windowRow.value?.throughput,
);
const wip = computed(() => windowDetail.value?.metrics?.wip ?? windowRow.value?.wip);

const windowLine = computed(() => {
  const days = windowRow.value?.throughput?.window_days ?? 14;
  return `Rolling ${days}-day window`;
});
const rangeLine = computed(() => {
  const r = windowRow.value;
  if (!r) return "";
  return `${fmtDate(r.sprint_start)} → ${fmtDate(r.sprint_end)}`;
});

onMounted(() => {
  if (projectKey.value) store.load(projectKey.value);
});
watch(
  () => projectKey.value,
  (k) => {
    if (k) store.load(k);
  },
);
watch(
  () => windowRow.value?.sprint_id,
  (id) => {
    if (id != null && projectKey.value) void detailStore.load(projectKey.value, id);
  },
  { immediate: true },
);
</script>

<style scoped>
.kanban-screen { position: relative; }
.kanban-hero {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 24px;
  margin-bottom: 36px;
}
.board-switch {
  display: inline-block;
  margin-top: 10px;
  font-size: 13px;
  color: var(--accent, #6d28d9);
  text-decoration: none;
}
.board-switch:hover { text-decoration: underline; }

.kanban-grid { display: flex; flex-direction: column; gap: 18px; }

.now-card {
  border-radius: 12px;
  padding: 18px 20px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
}
.now-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.lbl-mono {
  font-size: 11px; font-weight: 600; text-transform: uppercase;
  letter-spacing: 0.06em; color: var(--fg-subtle, #8a8f9c);
}
.now-title { margin: 6px 0 0; font-size: 22px; font-weight: 700; color: var(--fg, #0b0c10); }
.window-range { margin: 4px 0 0; font-size: 13px; color: var(--fg-muted, #525663); }

.error-state, .empty { padding: 24px 0; }
.error-title { font-weight: 700; }
.error-msg { color: var(--fg-muted, #525663); }
.skeletons { display: flex; flex-direction: column; gap: 12px; }
.skel-row { height: 60px; border-radius: 10px; background: var(--bg-elev); }
.skel-row.big { height: 120px; }
</style>
